/**
 * Pure handshake state for the onboarding connect step.
 * Maps form completeness + create/test API results → three check states.
 * Never invents a timer — states come from real form/API outcomes.
 */

/** @typedef {'wait'|'ok'|'fail'} HsState */

/**
 * Required fields present in the form values?
 * @param {string} slug
 * @param {Record<string, string>} values
 * @param {{ fields?: { key: string, optional?: boolean }[] }|null|undefined} schema
 */
export function credentialParsed(slug, values, schema) {
  const input = values || {};
  if (!schema || !Array.isArray(schema.fields)) {
    return Boolean(String(input.value || '').trim());
  }
  return schema.fields
    .filter((field) => !field.optional)
    .every((field) => String(input[field.key] || '').trim());
}

/**
 * Derive the three handshake rows from a create/test outcome.
 * @param {{
 *   parsed?: boolean,
 *   createStatus?: number,
 *   createError?: string,
 *   test?: {
 *     healthy?: boolean,
 *     reachable?: boolean,
 *     status?: string,
 *     latency_ms?: number,
 *     tool_count?: number,
 *     message?: string,
 *     error?: string,
 *   }|null,
 * }} opts
 */
export function handshakeFromResult(opts) {
  const parsed = opts && opts.parsed ? 'ok' : 'wait';
  if (parsed !== 'ok') {
    return { parsed: 'wait', reachable: 'wait', sample: 'wait' };
  }

  const createStatus = opts.createStatus;
  const createError = opts.createError;
  const test = opts.test;

  // Create failed before anything was saved.
  if (createError && !test) {
    const unreachable = createStatus === 502 || createStatus === 504;
    if (unreachable) {
      return {
        parsed: 'ok',
        reachable: 'fail',
        sample: 'wait',
        error: createError,
      };
    }
    return {
      parsed: 'ok',
      reachable: 'fail',
      sample: 'wait',
      error: createError,
    };
  }

  if (!test) {
    return { parsed: 'ok', reachable: 'wait', sample: 'wait' };
  }

  if (test.error && test.healthy == null && test.reachable == null) {
    return {
      parsed: 'ok',
      reachable: 'fail',
      sample: 'wait',
      error: test.error || test.message || 'Test failed',
      latency_ms: test.latency_ms,
      tool_count: test.tool_count,
    };
  }

  const reachable = test.reachable === false || test.status === 'unreachable'
    ? 'fail'
    : 'ok';
  let sample = 'wait';
  if (reachable === 'ok') {
    sample = test.healthy ? 'ok' : 'fail';
  }
  const error = sample === 'fail' || reachable === 'fail'
    ? (test.message || test.error || 'Handshake failed')
    : '';
  return {
    parsed: 'ok',
    reachable,
    sample,
    latency_ms: test.latency_ms,
    tool_count: test.tool_count,
    error,
  };
}

/**
 * Fail at a specific check (for tests and controlled UI states).
 * @param {'parsed'|'reachable'|'sample'} at
 */
export function handshakeFailAt(at) {
  if (at === 'parsed') {
    return { parsed: 'fail', reachable: 'wait', sample: 'wait' };
  }
  if (at === 'reachable') {
    return { parsed: 'ok', reachable: 'fail', sample: 'wait' };
  }
  return { parsed: 'ok', reachable: 'ok', sample: 'fail' };
}

/**
 * All three checks passed.
 */
export function handshakePass(extra) {
  return {
    parsed: 'ok',
    reachable: 'ok',
    sample: 'ok',
    latency_ms: extra && extra.latency_ms,
    tool_count: extra && extra.tool_count,
  };
}
